import { createSelector, createReducer } from '@reduxjs/toolkit';
import { takeEvery, call, all, fork } from 'redux-saga/effects';
import { updateDtoList, removeItemFromList } from '../helpers/reducerHelpers';
import {
  getRequestData,
  getIncludedData,
  selectAllFromApiState,
} from '../helpers/selectorHelpers';
import { UserHearingRoleConnector } from '../connectors/userHearingRoleConnector.js';
import {
  generateCreateApiCall,
  generateUpdateApiCall,
} from '../helpers/sagaHelpers';
import { getRelId, getEntityId } from '../helpers/apiHelpers';
import { userByIdSelector } from './userSlice';
import { getCurrentUserSuccess } from '../authenticationSlice';
import {
  HEARINGROLE_OWNER,
  USERHEARINGROLE_INCLUDE,
} from '../../constants/api';
import { hearingRoleByRoleSelector } from './hearingRoleSlice';
import {
  addUserHearingRolesToStore,
  createHearingSuccess,
  createUserHearingRoleInitialize,
  createUserHearingRoleSuccess,
  deleteUserHearingRoleInitialize,
  deleteUserHearingRoleSuccess,
  getAllHearingsSuccess,
  getAllUsersSuccess,
  getHearingSuccess,
  getPaginatedHearingsSuccess,
  removeUserHearingRolesFromStore,
  uploadCsvInviteesSuccess,
  uploadExcelInviteesSuccess,
  uploadInvitationGroupInviteesSuccess,
  uploadPersonalInviteesSuccess,
  uploadReviewersSuccess,
} from '../actions/api';

// -------- Overview --------

// The following endpoints exist
//  - POST /hearing/{hearingId}/userHearingRole
//  - DELETE /hearing/{hearingId}/userHearingRole/{id}
//
// UserHearingRole is included in the following endpoints
//  - GET /user
//  - GET /user/{id}
//  - GET /hearing
//  - GET /hearing/{id}
//  - GET /hearing/{hearingId}/comment

// -------- Reducer --------
const userHearingRoleReducer = createReducer(
  {
    all: [],
  },
  (builder) => {
    builder
      .addCase(createUserHearingRoleSuccess, (state, action) => {
        const userHearingRole = getRequestData(action);
        return updateDtoList(state, 'all', userHearingRole);
      })
      .addCase(deleteUserHearingRoleSuccess, (state, action) => {
        const data = getRequestData(action);
        const userHearingRoleIdsToDelete = data.map(
          (userHearingRole) => userHearingRole.id,
        );
        return removeItemFromList(state, 'all', userHearingRoleIdsToDelete);
      })
      .addCase(getAllUsersSuccess, (state, action) => {
        const data = getIncludedData(action);
        const userHearingRole = data.filter(
          (include) => include.type === USERHEARINGROLE_INCLUDE,
        );
        return updateDtoList(state, 'all', userHearingRole);
      })
      .addCase(createHearingSuccess, (state, action) => {
        const data = getIncludedData(action);
        const userHearingRoles = data.filter(
          (include) => include.type === USERHEARINGROLE_INCLUDE,
        );
        return updateDtoList(state, 'all', userHearingRoles);
      })
      .addCase(getAllHearingsSuccess, (state, action) => {
        const data = getIncludedData(action);
        const userHearingRoles = data.filter(
          (include) => include.type === USERHEARINGROLE_INCLUDE,
        );
        return updateDtoList(state, 'all', userHearingRoles);
      })
      .addCase(getHearingSuccess, (state, action) => {
        const data = getIncludedData(action);
        const userHearingRoles = data.filter(
          (include) => include.type === USERHEARINGROLE_INCLUDE,
        );
        return updateDtoList(state, 'all', userHearingRoles);
      })
      .addCase(removeUserHearingRolesFromStore, (state, action) => {
        const idToRemove = action.payload;
        return removeItemFromList(state, 'all', idToRemove);
      })
      .addCase(uploadReviewersSuccess, (state, action) => {
        const userHearingRoles = getRequestData(action);
        return updateDtoList(state, 'all', userHearingRoles);
      })
      .addCase(addUserHearingRolesToStore, (state, action) => {
        return updateDtoList(state, 'all', action.payload);
      })
      .addCase(getCurrentUserSuccess, (state, action) => {
        const data = getIncludedData(action);
        const userHearingRoles = data.filter(
          (include) => include.type === USERHEARINGROLE_INCLUDE,
        );
        return updateDtoList(state, 'all', userHearingRoles);
      })
      .addCase(getPaginatedHearingsSuccess, (state, action) => {
        const data = getIncludedData(action);
        const userHearingRoles = data.filter(
          (include) => include.type === USERHEARINGROLE_INCLUDE,
        );
        return updateDtoList(state, 'all', userHearingRoles);
      })
      .addCase(uploadCsvInviteesSuccess, (state, action) => {
        const data = getIncludedData(action);
        const userHearingRoles = data.filter(
          (include) => include.type === USERHEARINGROLE_INCLUDE,
        );
        return updateDtoList(state, 'all', userHearingRoles);
      })
      .addCase(uploadExcelInviteesSuccess, (state, action) => {
        const data = getIncludedData(action);
        const userHearingRoles = data.filter(
          (include) => include.type === USERHEARINGROLE_INCLUDE,
        );
        return updateDtoList(state, 'all', userHearingRoles);
      })
      .addCase(uploadPersonalInviteesSuccess, (state, action) => {
        const data = getIncludedData(action);
        const userHearingRoles = data.filter(
          (include) => include.type === USERHEARINGROLE_INCLUDE,
        );
        return updateDtoList(state, 'all', userHearingRoles);
      })
      .addCase(uploadInvitationGroupInviteesSuccess, (state, action) => {
        const data = getIncludedData(action);
        const userHearingRoles = data.filter(
          (include) => include.type === USERHEARINGROLE_INCLUDE,
        );
        return updateDtoList(state, 'all', userHearingRoles);
      });
  },
);

// -------- Sagas --------
export function* userHearingRoleSaga() {
  yield all([fork(createUserHearingRoleSaga), fork(updateUserHearingRoleSaga)]);
}
export function* createUserHearingRoleSaga() {
  yield takeEvery(
    createUserHearingRoleInitialize.type,
    generateCreateApiCall(UserHearingRoleConnector, function* (
      connector,
      action,
    ) {
      return yield call(
        [connector, connector.createUserHearingRole],
        action.payload.hearingId,
        {
          data: action.payload.model,
        },
      );
    }),
  );
}

export function* updateUserHearingRoleSaga() {
  yield takeEvery(
    deleteUserHearingRoleInitialize.type,
    generateUpdateApiCall(UserHearingRoleConnector, function* (
      connector,
      action,
      requestData,
    ) {
      return yield call(
        [connector, connector.updateUserHearingRole],
        action.payload.model.id,
        action.payload.hearingId,
        requestData,
      );
    }),
  );
}

// -------- Selectors --------

const selectAllUserHearingRoles = (state) =>
  selectAllFromApiState(state, 'userHearingRole');

const selectGetUserHearingRoleById = (state, props) => {
  const userHearingRoles = selectAllFromApiState(state, 'userHearingRole');
  return userHearingRoles.find(
    (userHearingRole) => userHearingRole.id === props.userHearingRoleId,
  );
};

const allUserHearingRolesSelector = createSelector(
  selectAllUserHearingRoles,
  (userHearingRoles) => userHearingRoles,
);

const allUserHearingRolesWithRoleSelector = (role) =>
  createSelector(
    [selectAllUserHearingRoles, hearingRoleByRoleSelector(role)],
    (userHearingRoles, hearingRole) =>
      userHearingRoles.filter(
        (uhr) => getRelId(uhr, 'hearingRole') === getEntityId(hearingRole),
      ),
  );

const allUserHearingRolesWithRoleByHearingIdSelector = (role, hearingId) =>
  createSelector(
    allUserHearingRolesWithRoleSelector(role),
    (userHearingRoles) =>
      userHearingRoles.filter(
        (userHearingRole) => getRelId(userHearingRole, 'hearing') === hearingId,
      ),
  );

const userHearingRoleOwnerByHearingId = (hearingId) =>
  createSelector(
    allUserHearingRolesWithRoleSelector(HEARINGROLE_OWNER),
    (userHearingRoles) =>
      userHearingRoles.find(
        (userHearingRole) => getRelId(userHearingRole, 'hearing') === hearingId,
      ),
  );

const userHearingRoleByIdSelector = createSelector(
  selectGetUserHearingRoleById,
  (userHearingRole) => userHearingRole,
);

const userHearingRolesByIdsSelector = (userHearingRoleIds) =>
  createSelector(allUserHearingRolesSelector, (userHearingRoles) =>
    userHearingRoles.filter((uhr) =>
      userHearingRoleIds.some((id) => getEntityId(uhr) === id),
    ),
  );

const isUserInHearingRoleSelector = (userId, hearingRole) =>
  createSelector(
    allUserHearingRolesWithRoleSelector(hearingRole),
    (userHearingRoles) =>
      userHearingRoles.some(
        (userHearingRole) => getRelId(userHearingRole, 'user') === userId,
      ),
  );

const allUserHearingRolesByHearingIdsSelector = (hearingIds) =>
  createSelector(selectAllUserHearingRoles, (userHearingRoles) =>
    userHearingRoles.filter(
      (uhr) => hearingIds && hearingIds.includes(getRelId(uhr, 'hearing')),
    ),
  );

const userHearingRoleOwnersByHearingIdsSelector = (hearingIds) =>
  createSelector(
    [
      allUserHearingRolesByHearingIdsSelector(hearingIds),
      hearingRoleByRoleSelector(HEARINGROLE_OWNER),
    ],
    (userHearingRoles, role) =>
      userHearingRoles.filter(
        (uhr) => getRelId(uhr, 'hearingRole') === getEntityId(role),
      ),
  );

const userHearingRolesByUserIdSelector = createSelector(
  userByIdSelector,
  (user) => {
    return user.included.filter(
      (include) => include.type === USERHEARINGROLE_INCLUDE,
    );
  },
);

// -------- Exporting --------
export {
  allUserHearingRolesSelector,
  userHearingRoleByIdSelector,
  userHearingRolesByIdsSelector,
  userHearingRolesByUserIdSelector,
  userHearingRoleOwnersByHearingIdsSelector,
  allUserHearingRolesWithRoleSelector,
  userHearingRoleOwnerByHearingId,
  isUserInHearingRoleSelector,
  allUserHearingRolesWithRoleByHearingIdSelector,
};
export default userHearingRoleReducer;
